import { layout, patchAt } from "@/lib/design";
import { SPECIAL, polygon, polyPath } from "@/lib/special";

const inp = "h-11 w-full rounded-[10px] border border-black/10 bg-white px-3 text-[15px] text-black outline-none focus:border-[#007AFF]";
const tile = (on) => `flex min-h-[44px] flex-col items-center gap-1 rounded-xl border-2 p-1.5 text-[11px] font-semibold transition-colors ${on ? "border-[#007AFF] bg-[#E8F1FD] text-[#007AFF]" : "border-black/10 bg-white text-black hover:border-[#007AFF]/50"}`;

// Phase 26: special-shape picture picker + the chosen shape's own sizes (overall W × H stays the piece size).
export function ShapePicker({ d, u, set }) {
  const sp = u.node.special || null;
  const pick = (k) => set(patchAt(d, u.path, k ? { special: { shape: k }, top: "rect", shape_id: null } : { special: null }));
  const def = SPECIAL.find(([k]) => k === sp?.shape);
  return (
    <div className="col-span-2 space-y-2" data-testid="piece-special-shape">
      <p className="text-[13px] font-semibold text-[#6D6D72]">Special shape <span className="font-normal">(+8% on the rectangle price)</span></p>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-1.5" role="radiogroup">
        <button type="button" role="radio" aria-checked={!sp} data-testid="special-shape-none" onClick={() => pick(null)} className={tile(!sp)}>
          <svg viewBox="-2 -2 44 44" className="h-9 w-9"><rect x="4" y="2" width="32" height="36" fill="#F2F2F7" stroke="currentColor" strokeWidth="2" /></svg>None
        </button>
        {SPECIAL.map(([k, label]) => (
          <button key={k} type="button" role="radio" aria-checked={sp?.shape === k} data-testid={`special-shape-${k}`} onClick={() => pick(k)} className={tile(sp?.shape === k)}>
            <svg viewBox="-2 -2 44 44" className="h-9 w-9"><path d={polyPath(polygon({ shape: k }, 36, 40), 2, 0)} fill="#F2F2F7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            <span className="text-center leading-tight">{label}</span>
          </button>
        ))}
      </div>
      {def && def[2].length > 0 && (
        <div className="grid grid-cols-2 gap-2" data-testid="special-shape-sizes">
          {def[2].map(([key, label]) => (
            <label key={key} className="block"><span className="mb-1 block text-[12px] font-semibold text-[#6D6D72]">{label}{key === "side" ? "" : " (in)"}</span>
              {key === "side" ? (
                <select data-testid={`special-param-${key}`} value={sp[key] || "left"} onChange={(e) => set(patchAt(d, u.path, { special: { ...sp, side: e.target.value } }))} className={inp}>
                  <option value="left">Left</option><option value="right">Right</option></select>
              ) : (
                <input data-testid={`special-param-${key}`} inputMode="decimal" defaultValue={sp[key] ?? ""} placeholder="auto" key={`${sp.shape}-${key}`}
                  onBlur={(e) => set(patchAt(d, u.path, { special: { ...sp, [key]: e.target.value === "" ? undefined : Number(e.target.value) } }))} className={inp} />
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Phase 26: series offering this piece type, priced from that series' supplier line; the main piece can apply its series to all.
export function SeriesPicker({ d, u, set, price }) {
  const L = layout(d), i = L.units.findIndex((x) => x.path.join() === u.path.join()), info = price?.pieces?.[i];
  if (!info?.series_options?.length) return null;
  const main = L.units.reduce((b, x) => (x.w * x.h > b.w * b.h ? x : b), L.units[0]).path.join() === u.path.join();
  const applyAll = () => {
    const series = info.line.split(" ")[0];
    let next = d;
    L.units.forEach((x, j) => {
      const opt = price.pieces[j]?.series_options?.find((o) => o.name.split(" ")[0] === series);
      if (opt) next = patchAt(next, x.path, { line_id: opt.id });
    });
    set(next);
  };
  return (
    <div className="col-span-2 space-y-1.5" data-testid="piece-series">
      <label className="block"><span className="mb-1 block text-[13px] font-semibold text-[#6D6D72]">Series</span>
        <select data-testid="piece-series-select" value={info.line_id} onChange={(e) => set(patchAt(d, u.path, { line_id: e.target.value }))} className={inp}>
          {info.series_options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </label>
      {main && L.units.length > 1 && (
        <button type="button" data-testid="piece-series-apply-all" onClick={applyAll}
          className="h-11 w-full rounded-full border border-black/10 bg-white text-[14px] font-medium text-black hover:border-[#007AFF]">Apply {info.line.split(" ")[0]} to all pieces</button>
      )}
    </div>
  );
}
